import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, Cell, XAxis, YAxis, ReferenceLine, LabelList, Tooltip
} from 'recharts';
import { fetchDailyProduction, fetchShotHistory, fetchMachinePph } from '../../../services/api';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh';
import { useTranslation } from '../../../context/LanguageContext';
import './ProductionNew.css';

/* ===== 품질·생산 > 생산 — PNG 시안「전사 > 한국 공장 > 사출기 > 품질 생산 > 생산」=====
 * 검수사진 탭(InspectionPhotos)의 서브 탭으로 렌더된다. 스타일은 pd- 접두어만 쓴다.
 * 구성: 생산량 추이(최근 14일 막대 + 기준선) / 오늘 실적 4칸 / 양품·불량 4칸.
 *
 * 데이터 출처
 * - 일별 생산량·양품·불량 : /machine/daily-production/{id}?days=14 — update_date_time 날짜별 샷 수/GOOD/BAD (샷이 없는 날은 0)
 *                          이 API 가 없는(미배포) 서버에서는 /machine/shot-history?limit=1000 을 날짜별로 묶어 대신 쓰고 "추정" 표기
 * - PPH                  : /machine/pph/{id} — 오늘 시간대별 샷 수. 생산이 있었던 시간대의 평균(추정) — 공정관리 탭과 같은 계산
 * - 가동률               : 설비 카드(/dashboard/machines) utilization — 24시간 타임라인 기준. 없으면 '—'
 * - 목표 대비            : 설비별 일일 목표치를 저장하는 곳이 없어 '—'. 시안의 "목표치" 기준선 대신 생산이 있었던 날의
 *                          14일 평균을 기준선으로 그리고 막대 위 숫자는 평균 대비 차이(추정)로 표기한다.
 * 생산량 = 샷 수(캐비티 수를 곱하지 않는다). 값이 null 이면 '—'. */

const DAYS = 14;
const SAMPLE_LIMIT = 1000;
const isNum = (v) => v != null && !Number.isNaN(Number(v));
const intFmt = (v) => (isNum(v) ? Number(v).toLocaleString() : '—');
const pct1 = (v) => (isNum(v) ? Number(v).toFixed(1) : '—');
const pad2 = (n) => String(n).padStart(2, '0');
const dateStr = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const COLORS = { above: '#47CD89', below: '#98A8BF', line: '#83A6FF', label: '#FFFFFF' };

/* 최근 DAYS 일 날짜 목록(오름차순, 오늘 포함) */
const lastDays = () => {
  const out = [];
  const today = new Date();
  for (let i = DAYS - 1; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    out.push(dateStr(d));
  }
  return out;
};

/* 폴백: 샷 이력을 createdAt 날짜별로 묶는다 (API 미배포 서버용 · 최근 SAMPLE_LIMIT 샷만 보이므로 추정) */
const groupShotsByDay = (shots) => {
  const map = {};
  (shots || []).forEach((s) => {
    if (!s?.createdAt) return;
    const d = new Date(s.createdAt);
    if (Number.isNaN(d.getTime())) return;
    const key = dateStr(d);
    if (!map[key]) map[key] = { date: key, totalShots: 0, goodCount: 0, badCount: 0 };
    map[key].totalShots += 1;
    if (s.qualityStatus === 'GOOD') map[key].goodCount += 1;
    else if (s.qualityStatus === 'BAD' || s.qualityStatus === 'DEFECT') map[key].badCount += 1;
  });
  return lastDays().map((date) => {
    const row = map[date] || { date, totalShots: 0, goodCount: 0, badCount: 0 };
    const judged = row.goodCount + row.badCount;
    return { ...row, goodRate: judged > 0 ? Math.round((row.goodCount * 1000) / judged) / 10 : null };
  });
};

function Kpi({ label, value, unit, tag, note }) {
  return (
    <div className="pd-kpi" title={note || undefined}>
      <span className="pd-kpi-label">{label}{tag && <em className="pd-tag">{tag}</em>}</span>
      <span className="pd-kpi-value">{value}{unit && <small>{unit}</small>}</span>
    </div>
  );
}

function ProductionNew({ equipmentId, machine }) {
  const { t } = useTranslation();
  const tr = useCallback((key, params) => t(`pages.monitoring.machine.production.${key}`, params), [t]);

  const [days, setDays] = useState([]);
  const [isSample, setIsSample] = useState(false);
  const [pph, setPph] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (silent = false) => {
    if (!equipmentId) return;
    if (!silent) { setLoading(true); setError(null); }
    const [dailyRes, pphRes] = await Promise.allSettled([
      fetchDailyProduction(equipmentId, DAYS),
      fetchMachinePph(equipmentId)
    ]);

    if (dailyRes.status === 'fulfilled' && Array.isArray(dailyRes.value?.days)) {
      setDays(dailyRes.value.days);
      setIsSample(false);
    } else {
      /* 일별 집계 API 가 없는 서버(404 등) → 샷 이력 폴백 */
      console.warn('Daily production API unavailable, falling back to shot history:', dailyRes.reason);
      try {
        const hist = await fetchShotHistory(equipmentId, SAMPLE_LIMIT);
        setDays(groupShotsByDay(hist?.shots));
        setIsSample(true);
      } catch (e) {
        console.error('Production fallback load failed:', e);
        if (!silent) setError(tr('loadFailed'));
      }
    }

    if (pphRes.status === 'fulfilled') setPph(pphRes.value);
    else console.error('Production pph load failed:', pphRes.reason);
    if (!silent) setLoading(false);
  }, [equipmentId, tr]);

  useEffect(() => { load(false); }, [load]);
  useAutoRefresh(() => load(true), !!equipmentId);

  /* 기준선 = 생산이 있었던 날의 평균 (설비별 목표치 없음 → 추정) */
  const average = useMemo(() => {
    const vals = days.map((d) => Number(d.totalShots)).filter((v) => Number.isFinite(v) && v > 0);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }, [days]);

  const chartRows = useMemo(() => days.map((d) => {
    const total = isNum(d.totalShots) ? Number(d.totalShots) : 0;
    const delta = average != null ? Math.round(total - average) : null;
    return {
      date: d.date,
      label: d.date ? d.date.slice(5).replace('-', '/') : '',
      total,
      delta,
      deltaLabel: delta == null ? '' : `${delta >= 0 ? '+ ' : '- '}${Math.abs(delta).toLocaleString()}`,
      above: average == null || total >= average
    };
  }), [days, average]);

  const today = days.length ? days[days.length - 1] : null;                       /* days 는 오름차순 · 마지막이 오늘 */
  const hasAnyShots = days.some((d) => Number(d.totalShots) > 0);

  /* PPH: 생산이 있었던 시간대의 평균 (공정관리 탭과 같은 계산 · 추정) */
  const pphValue = useMemo(() => {
    const hours = (pph?.hourlyData || []).filter((h) => isNum(h?.count) && Number(h.count) > 0);
    if (hours.length === 0) return null;
    return Math.round(hours.reduce((a, h) => a + Number(h.count), 0) / hours.length);
  }, [pph]);

  const utilization = isNum(machine?.utilization) ? Number(machine.utilization) : null;   /* 설비 카드 utilization (타임라인 기준) */

  const renderChart = () => {
    if (loading && days.length === 0) return <div className="pd-message">{tr('loading')}</div>;
    if (error && days.length === 0) return <div className="pd-message pd-message-error">{error}</div>;
    if (!hasAnyShots) return <div className="pd-message">{tr('empty')}</div>;
    return (
      <div className="pd-chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartRows} margin={{ top: 28, right: 72, left: 8, bottom: 4 }} barCategoryGap="32%">
            <XAxis dataKey="label" tick={{ fill: '#C5D7E0', fontSize: 14 }} axisLine={false} tickLine={false} interval={0} />
            <YAxis hide domain={[0, (max) => Math.max(1, Math.ceil(max * 1.12))]} />
            <Tooltip
              cursor={{ fill: 'rgba(255,255,255,0.04)' }}
              contentStyle={{ backgroundColor: '#1A1E24', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '4px', fontSize: '12px' }}
              labelStyle={{ color: '#98A8BF' }}
              formatter={(v) => [`${Number(v).toLocaleString()} ${tr('unitShot')}`, tr('production')]}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.date || ''}
            />
            {average != null && (
              <ReferenceLine
                y={average}
                stroke={COLORS.line}
                strokeDasharray="4 4"
                label={{ value: tr('avgLine'), position: 'right', fill: '#FFFFFF', fontSize: 12 }}
              />
            )}
            <Bar dataKey="total" radius={[2, 2, 0, 0]} isAnimationActive={false}>
              {chartRows.map((row) => <Cell key={row.date} fill={row.above ? COLORS.above : COLORS.below} />)}
              <LabelList dataKey="deltaLabel" position="top" fill={COLORS.label} fontSize={14} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <div className="pd-root">
      {/* ---- 생산량 추이 (최근 14일) ---- */}
      <section className="pd-panel">
        <div className="pd-panel-head">
          <h3 className="pd-panel-title">{tr('trendTitle')}</h3>
          <span className="pd-note">{isSample ? tr('sampleNote', { n: SAMPLE_LIMIT.toLocaleString() }) : tr('trendNote')}</span>
        </div>
        {renderChart()}
      </section>

      {/* ---- 오늘 실적 ---- */}
      <section className="pd-panel">
        <h3 className="pd-panel-title">{tr('todayTitle')}</h3>
        <div className="pd-kpis">
          <Kpi label={tr('production')} value={intFmt(today?.totalShots)} unit={tr('unitShot')} tag={isSample ? tr('estimated') : null} />   {/* daily-production.days[today].totalShots */}
          <Kpi label={tr('targetRatio')} value="—" note={tr('noTarget')} />                                                                  {/* 설비별 목표치 없음 */}
          <Kpi label={tr('pph')} value={intFmt(pphValue)} unit={tr('unitShotPerHour')} tag={tr('estimated')} />                             {/* pph.hourlyData 평균 */}
          <Kpi label={tr('utilization')} value={pct1(utilization)} unit="%" />                                                               {/* 설비 카드 utilization */}
        </div>
      </section>

      {/* ---- 양품 / 불량 ---- */}
      <section className="pd-panel">
        <h3 className="pd-panel-title">{tr('qualityTitle')}</h3>
        <div className="pd-kpis">
          <Kpi label={tr('production')} value={intFmt(today?.totalShots)} unit={tr('unitShot')} tag={isSample ? tr('estimated') : null} />
          <Kpi label={tr('goodRate')} value={pct1(today?.goodRate)} unit="%" />                                                              {/* good / (good+bad) */}
          <Kpi label={tr('pph')} value={intFmt(pphValue)} unit={tr('unitShotPerHour')} tag={tr('estimated')} />
          <Kpi label={tr('defect')} value={intFmt(today?.badCount)} unit={tr('unitShot')} />                                                 {/* quality_status = BAD */}
        </div>
        <p className="pd-footnote">{tr('kpiNote')}</p>
      </section>
    </div>
  );
}

export default ProductionNew;
